import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ConversionWindow, CountType, FunnelOrder} from 'common/report/models/funnels/types';
import {formatPercent} from 'common/report/util/funnels/utils';
import {capitalize, pluralize} from 'common/util/string';
import {COUNT_TYPE_OPTIONS, ORDER_OPTIONS} from 'common/report/models/funnels/constants';

import template from './index.jade';
import css from './index.styl?inline';

export const OVERVIEW_PANEL_WIDTH = 230;

export interface OverviewPanelAttrs {
  'conversion-label': string;
  conversion: number;
  'conversion-window': ConversionWindow;
  'count-type': CountType;
  'hide-left-padding'?: boolean;
  'funnel-order': FunnelOrder;
  'overview-width'?: number;
  'past-conversion'?: number;
}

export class OverviewPanel extends Component<EmptyObj, OverviewPanelAttrs> {
  static get attrsSchema(): AttrsSchema<OverviewPanelAttrs> {
    return {
      ...super.attrsSchema,
      'conversion-label': {type: `string`, default: `Overall Conversion`},
      conversion: {type: `number`},
      'conversion-window': {type: `json`, default: {}, tsType: `ConversionWindow`},
      'count-type': {type: `string`, enum: [`unique`, `general`, `session`]},
      'hide-left-padding': `boolean`,
      'funnel-order': {type: `string`, enum: [`loose`, `any`]},
      'overview-width': {type: `number`, default: OVERVIEW_PANEL_WIDTH},
      'past-conversion': {type: `number`, default: undefined},
    };
  }
  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {},
      helpers: {
        formatPercent,
        getCountTypeText: () => {
          const countType = this.attr(`count-type`);
          return COUNT_TYPE_OPTIONS.find((o) => o.value === countType)?.label;
        },
        getOrderText: () => {
          const funnelOrder = this.attr(`funnel-order`);
          const lowercase = ORDER_OPTIONS.find((o) => o.value === funnelOrder)?.label ?? ``;
          return lowercase
            .split(` `)
            .map((word) => capitalize(word))
            .join(` `);
        },
        getConversionWindowText: () => {
          const window = this.attr(`conversion-window`);
          return `${window.value} ${pluralize(window.unit, window.value)}`;
        },
        getPastConversionDifference: () => {
          const difference = this.attr(`conversion`) - this.attr(`past-conversion`);
          let percentText = formatPercent(difference, 2);
          if (difference >= 0) {
            percentText = `+${percentText}`;
          }
          return percentText;
        },
        getStyles: () => {
          const paddingLeft = 28;
          let overviewPanelWidth = this.attr(`overview-width`) || OVERVIEW_PANEL_WIDTH;

          if (!this.attr(`hide-left-padding`)) {
            overviewPanelWidth -= paddingLeft;
          }

          return {
            ...(this.attr(`hide-left-padding`) ? {} : {'padding-left': `${paddingLeft}px`}),
            width: `${overviewPanelWidth}px`,
          };
        },
      },
    };
  }
}

export const FunnelOverviewPanelEl = defineAndMakeJsxFactory(`funnel-overview-panel`, OverviewPanel);
